import { SEVERITIES, type Severity } from '@zevik/schemas';

/**
 * How a band is shown, with **the shape and the word carrying it and colour
 * only reinforcing** (docs/DECISIONS.md ZD-112).
 *
 * ## Why this is not a taste question
 *
 * A band is the most quotable thing this product emits, and the person reading it
 * is a reviewer making a call they will be held to. Encoding it in hue alone
 * fails for roughly one in twelve men, fails in greyscale print, and fails in
 * every screenshot pasted into a document. So each band carries three
 * independent signals: the word itself, always spelled out; a **filled-segment
 * meter** whose count differs per band; and a weight. Remove every colour from
 * this file and the five bands remain distinguishable, which is the test
 * `severity.test.ts` applies rather than a claim made here.
 *
 * The meter is drawn from `SEVERITIES`' own position, so a band added to the enum
 * gets a distinct count without anybody choosing one — and a sixth band would
 * make `SEGMENTS` too small, which is why that is asserted rather than assumed.
 */

/** The number of filled segments per band: five for `critical` down to one. */
export const SEGMENTS = 5;

/**
 * Filled segments for a band — strongest gets the most.
 *
 * `SEVERITIES` is declared strongest-first, so this is `SEGMENTS` minus the
 * band's index. Derived rather than tabulated: a table beside the enum is the
 * second copy that disagrees the day the enum moves.
 */
export function filledSegments(band: Severity): number {
  const index = SEVERITIES.indexOf(band);
  if (index < 0) {
    throw new Error(`not a severity band: ${band}`);
  }
  if (index >= SEGMENTS) {
    throw new Error(
      `SEVERITIES has more members than SEGMENTS can distinguish: ${band} is at index ` +
        `${String(index)} of ${String(SEGMENTS)} segments. A band nobody can tell apart from ` +
        `another is worse than an unstyled one — raise SEGMENTS and check the meter still reads.`,
    );
  }
  return SEGMENTS - index;
}

/**
 * The meter as text, so it survives a copy-paste and a screen reader's fallback.
 *
 * Filled and hollow are different **glyphs** rather than the same glyph in two
 * colours, which is the whole point.
 */
export function bandMeter(band: Severity): string {
  const filled = filledSegments(band);
  return '▰'.repeat(filled) + '▱'.repeat(SEGMENTS - filled);
}

/**
 * What a band is called in an `aria-label`, spelled out rather than left to the
 * meter's glyphs, which read as punctuation.
 */
export function bandLabel(band: Severity): string {
  return `severity ${band}, ${String(filledSegments(band))} of ${String(SEGMENTS)}`;
}
